import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./MonthlyExtensionCount.module.css";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { CODE_COUNTRY_FULL_NAME, ALL_COUNTRIES_WITH_JDWIN } from "../../../constants/constant";
import { fetchAdminMonthlyExtensionReport } from "../../../store/slices/dashboard";

const MonthlyExtensionCount = ({ isAdmin = false }) => {
  const dispatch = useDispatch();
  const { monthlyExtensionReport: adminMonthlyExtensionReport, monthlyExtensionReportLoading: adminMonthlyExtensionReportLoading } = useSelector(
    (state) => state.adminDashboard
  );
  
  const [selectedCountry, setSelectedCountry] = useState("");
  const [chartData, setChartData] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (isAdmin) {
      // Add "ALL" option to countries
      const allCountries = ['ALL', ...ALL_COUNTRIES_WITH_JDWIN];
      setCountries(allCountries);
      setSelectedCountry(allCountries[0]); // Default to "ALL"
    }
  }, [isAdmin]);
  useEffect(() => {
    if (isAdmin && selectedCountry) {
      dispatch(fetchAdminMonthlyExtensionReport({ country: selectedCountry }));
    }
  }, [isAdmin, selectedCountry, dispatch]);
  useEffect(() => {
    if (isAdmin && adminMonthlyExtensionReport) {
      if (adminMonthlyExtensionReport.extensionRequestList && adminMonthlyExtensionReport.extensionRequestList.length > 0) {
        processChartData(adminMonthlyExtensionReport.extensionRequestList);
      } else {
        setChartData([]);
      }
    }
  }, [isAdmin, adminMonthlyExtensionReport]);

  useEffect(() => {
    if (!isAdmin) {
      fetchMonthlyData();
    }
  }, [isAdmin]);
  useEffect(() => {
    if (!isAdmin && selectedCountry) {
      fetchMonthlyData();
    }
  }, [selectedCountry, isAdmin]);

  const fetchMonthlyData = async () => {
    const { getDataWAuth } = await import("../../../utils/api-service");
    const { CCM_DASHBOARD_MONTHLY_EXTENSION_REPORT } = await import("../../../constants/constant");
    
    setLoading(true);
    try {
      const url = selectedCountry 
        ? `${CCM_DASHBOARD_MONTHLY_EXTENSION_REPORT}?country=${selectedCountry}`
        : CCM_DASHBOARD_MONTHLY_EXTENSION_REPORT;
      
      const response = await getDataWAuth(url);
      
      if (response.data) {
        if (!isAdmin && response.data.country && response.data.country.length > 0) {
          setCountries(response.data.country);
          if (!selectedCountry) {
            setSelectedCountry(response.data.country[0]);
          }
        }
        if (response.data.extensionRequestList && response.data.extensionRequestList.length > 0) {
          processChartData(response.data.extensionRequestList);
        } else {
          setChartData([]);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching monthly extension data:', error);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  const isLoading = isAdmin ? adminMonthlyExtensionReportLoading : loading;

  const processChartData = (extensionRequests) => {
    const currentDate = new Date();
    const currentCalendarMonth = currentDate.getMonth() + 1; // 1-12
    const currentCalendarYear = currentDate.getFullYear();
    let currentFiscalMonth = currentCalendarMonth - 1;
    let currentFiscalYear = currentCalendarYear;
    if (currentFiscalMonth <= 0) {
      currentFiscalMonth = 12;
      currentFiscalYear -= 1;
    }
    const monthlyData = {};
    for (let i = 11; i >= 0; i--) {
      let fiscalMonth = currentFiscalMonth - i;
      let fiscalYear = currentFiscalYear;
      
      while (fiscalMonth <= 0) {
        fiscalMonth += 12;
        fiscalYear -= 1;
      }
      
      const key = `${fiscalMonth}-${fiscalYear}`;
      monthlyData[key] = {
        fiscalMonth: key,
        fiscalMonthNum: fiscalMonth,
        fiscalYear: fiscalYear,
        IE: 0,
        EF: 0
      };
    }
    extensionRequests.forEach(request => {
      if (request.processingtime && (request.extensionType === 'IE' || request.extensionType === 'EF')) {
        const processingDate = new Date(request.processingtime);
        const calendarMonth = processingDate.getMonth() + 1;
        const calendarYear = processingDate.getFullYear();
        let fiscalMonth = calendarMonth - 1;
        let fiscalYear = calendarYear;
        
        if (fiscalMonth <= 0) {
          fiscalMonth = 12;
          fiscalYear -= 1;
        }        
        const key = `${fiscalMonth}-${fiscalYear}`;
        if (monthlyData[key]) {
          if (request.extensionType === 'IE') {
            monthlyData[key].IE += 1;
          } else if (request.extensionType === 'EF') {
            monthlyData[key].EF += 1;
          }
        }
      }
    });
    const sortedData = Object.values(monthlyData).sort((a, b) => {
      if (a.fiscalYear !== b.fiscalYear) return a.fiscalYear - b.fiscalYear;
      return a.fiscalMonthNum - b.fiscalMonthNum;
    });
    const chartDataArray = sortedData.map(item => {
      let calendarMonth = item.fiscalMonthNum + 1;
      if (calendarMonth > 12) {
        calendarMonth = 1;
      }
      
      return {
        month: getMonthLabel(calendarMonth),
        IE: item.IE,
        EF: item.EF
      };
    });

    setChartData(chartDataArray);
  };

  const getMonthLabel = (month) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return monthNames[month - 1];
  };

  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setSelectedCountry(newCountry);
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <h3 className={styles.title}>Monthly Extension Count</h3>
        <div className={styles.filterSection}>
          <div className={styles.countryIndicators}>
            <span className={styles.indicator}>
              <span className={styles.dot} style={{ backgroundColor: '#000000' }}></span>
              <span className={styles.label}>IE</span>
            </span>
            <span className={styles.indicator}>
              <span className={styles.dot} style={{ backgroundColor: '#367c2b' }}></span>
              <span className={styles.label}>EF</span>
            </span>
          </div>
          <select 
            className={styles.dropdown}
            value={selectedCountry}
            onChange={handleCountryChange}
          >
            {countries.map((country) => (
              <option key={country} value={country}>
                {country === 'ALL' ? 'All Countries' : (CODE_COUNTRY_FULL_NAME[country] || country)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.chartContainer}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <p>Loading chart data...</p>
          </div>
        ) : chartData.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <p>No data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 12, fill: '#666' }}
                axisLine={{ stroke: '#e0e0e0' }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#666' }}
                axisLine={{ stroke: '#e0e0e0' }}
                domain={[0, 'dataMax + 100']}
                allowDecimals={false}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="line"
              />
              <Line 
                type="monotone" 
                dataKey="IE" 
                stroke="#000000" 
                strokeWidth={2}
                dot={{ fill: '#000000', r: 4 }}
                activeDot={{ r: 6 }}
                name="IE"
              />
              <Line 
                type="monotone" 
                dataKey="EF" 
                stroke="#367c2b" 
                strokeWidth={2}
                dot={{ fill: '#367c2b', r: 4 }}
                activeDot={{ r: 6 }}
                name="EF"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default MonthlyExtensionCount;
